import React, { useState, useEffect } from "react";
import "../styles/PricingManagement.css";

interface PricingRule {
  id: number;
  startDate: string;
  endDate: string;
  price: number;
  description: string;
}

interface PricingManagementProps {
  credentials: {
    username: string;
    password: string;
  };
}

export const PricingManagement: React.FC<PricingManagementProps> = ({
  credentials,
}) => {
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);
  const [newRule, setNewRule] = useState({
    startDate: "",
    endDate: "",
    price: 0,
    description: "",
  });
  const [error, setError] = useState("");

  const fetchRules = async () => {
    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/pricing-rules",
        "https://api.nameren-rai.com/api/admin/pricing-rules",
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
        }
      );
      const data = await response.json();
      setRules(data);
    } catch (error) {
      setError("Грешка при зареждане на правилата за цени");
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleCreateRule = async () => {
    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/pricing-rules",
        "https://api.nameren-rai.com/api/admin/pricing-rules",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
          body: JSON.stringify(newRule),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Грешка при създаване на правило");
      }

      setNewRule({ startDate: "", endDate: "", price: 0, description: "" });
      fetchRules();
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Грешка при създаване на правило"
      );
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;

    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/pricing-rules/${editingRule.id}",
        `https://api.nameren-rai.com/api/admin/pricing-rules/${editingRule.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
          body: JSON.stringify(editingRule),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Грешка при обновяване на правило");
      }

      setEditingRule(null);
      fetchRules();
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Грешка при обновяване на правило"
      );
    }
  };

  const handleDeleteRule = async (id: number) => {
    if (!window.confirm("Сигурни ли сте, че искате да изтриете това правило?"))
      return;

    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/pricing-rules/${id}",
        `https://api.nameren-rai.com/api/admin/pricing-rules/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Грешка при изтриване на правило");
      }

      fetchRules();
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Грешка при изтриване на правило"
      );
    }
  };

  return (
    <div className="pricing-management">
      <h3>Управление на цените</h3>

      {error && <div className="error-message">{error}</div>}

      <div className="rules-list">
        <table>
          <thead>
            <tr>
              <th>Описание</th>
              <th>Начална дата</th>
              <th>Крайна дата</th>
              <th>Цена</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td>{rule.description}</td>
                <td>{new Date(rule.startDate).toLocaleDateString()}</td>
                <td>{new Date(rule.endDate).toLocaleDateString()}</td>
                <td>{rule.price}€</td>
                <td>
                  <button onClick={() => setEditingRule(rule)}>
                    Редактирай
                  </button>
                  <button onClick={() => handleDeleteRule(rule.id)}>
                    Изтрий
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rule-form">
        <h4>
          {editingRule ? "Редактиране на правило" : "Добавяне на ново правило"}
        </h4>
        <div className="form-group">
          <label>Описание:</label>
          <input
            type="text"
            value={editingRule ? editingRule.description : newRule.description}
            onChange={(e) => {
              if (editingRule) {
                setEditingRule({ ...editingRule, description: e.target.value });
              } else {
                setNewRule({ ...newRule, description: e.target.value });
              }
            }}
          />
        </div>
        <div className="form-group">
          <label>Начална дата:</label>
          <input
            type="date"
            value={editingRule ? editingRule.startDate : newRule.startDate}
            onChange={(e) => {
              if (editingRule) {
                setEditingRule({ ...editingRule, startDate: e.target.value });
              } else {
                setNewRule({ ...newRule, startDate: e.target.value });
              }
            }}
          />
        </div>
        <div className="form-group">
          <label>Крайна дата:</label>
          <input
            type="date"
            value={editingRule ? editingRule.endDate : newRule.endDate}
            onChange={(e) => {
              if (editingRule) {
                setEditingRule({ ...editingRule, endDate: e.target.value });
              } else {
                setNewRule({ ...newRule, endDate: e.target.value });
              }
            }}
          />
        </div>
        <div className="form-group">
          <label>Цена:</label>
          <input
            type="number"
            value={editingRule ? editingRule.price : newRule.price}
            onChange={(e) => {
              if (editingRule) {
                setEditingRule({
                  ...editingRule,
                  price: Number(e.target.value),
                });
              } else {
                setNewRule({ ...newRule, price: Number(e.target.value) });
              }
            }}
          />
        </div>
        <div className="form-buttons">
          {editingRule ? (
            <>
              <button onClick={handleUpdateRule}>Запази промените</button>
              <button onClick={() => setEditingRule(null)}>Отказ</button>
            </>
          ) : (
            <button onClick={handleCreateRule}>Добави правило</button>
          )}
        </div>
      </div>
    </div>
  );
};
